import { Post, ServiceItem, NewsItem, Product, Order, Category, CategoryWithChildren, CategoryImage } from '../types';

const API_BASE = '/api';

/**
 * ApiError — unified error type thrown by every function in this module.
 *
 * - `status` is the HTTP status code, or `0` for network/parse failures.
 * - `message` is taken from the backend `{"error":"..."}` body when present,
 *   otherwise falls back to `statusText` or a generic network message.
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** True when no HTTP response was received (offline, DNS, CORS, etc.) */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

function getHeaders(token: string | null): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Centralized non-OK response handler. Reads `{"error":"..."}` from the body
 * when possible, falls back to `statusText`. Also fires `auth:unauthorized`
 * when an authenticated request returns 401 so the AuthContext can log out.
 */
async function handleErrorResponse(res: Response, sentAuth: boolean): Promise<never> {
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const text = await res.text();
    if (text) {
      try {
        const data = JSON.parse(text);
        if (data && typeof data.error === 'string' && data.error.length > 0) {
          message = data.error;
        } else if (typeof data === 'string' && data.length > 0) {
          message = data;
        }
      } catch {
        // Not JSON — use raw text if it's short and looks human-readable
        if (text.length > 0 && text.length < 500) {
          message = text;
        }
      }
    }
  } catch {
    // ignore body read failures
  }

  if (res.status === 401 && sentAuth) {
    try {
      window.dispatchEvent(new Event('auth:unauthorized'));
    } catch {
      // jsdom / non-browser env
    }
  }

  throw new ApiError(message, res.status);
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const sentAuth = hasAuthHeader(options?.headers);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, options);
  } catch (e: any) {
    throw new ApiError(e?.message || 'Network error', 0);
  }
  if (!res.ok) {
    await handleErrorResponse(res, sentAuth);
  }
  // 204 No Content — return undefined cast as T
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  try {
    return await res.json();
  } catch (e: any) {
    throw new ApiError(e?.message || 'Invalid JSON response', 0);
  }
}

/** Variant of `request` for endpoints that don't return JSON. */
async function requestVoid(path: string, options?: RequestInit): Promise<void> {
  const sentAuth = hasAuthHeader(options?.headers);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, options);
  } catch (e: any) {
    throw new ApiError(e?.message || 'Network error', 0);
  }
  if (!res.ok) {
    await handleErrorResponse(res, sentAuth);
  }
}

function hasAuthHeader(headers: HeadersInit | undefined): boolean {
  if (!headers) return false;
  if (headers instanceof Headers) {
    return headers.has('Authorization');
  }
  if (Array.isArray(headers)) {
    return headers.some(([k]) => k.toLowerCase() === 'authorization');
  }
  return Object.keys(headers as Record<string, string>).some(
    k => k.toLowerCase() === 'authorization'
  );
}

// --- Auth ---
export async function login(password: string): Promise<{ token: string }> {
  return request('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
}

// --- Posts ---
export async function getPosts(): Promise<Post[]> {
  return request('/posts');
}

export async function getPostById(id: number): Promise<Post> {
  return request(`/posts/${id}`);
}

export async function createPost(token: string, data: Omit<Post, 'id' | 'sortOrder'>): Promise<Post> {
  return request('/posts', {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function deletePost(token: string, id: number): Promise<void> {
  return requestVoid(`/posts/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

export async function updatePost(token: string, id: number, data: Partial<Post>): Promise<Post> {
  return request(`/posts/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function reorderPosts(token: string, items: { id: number; sortOrder: number }[]): Promise<void> {
  return requestVoid(`/posts/reorder`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(items),
  });
}

// --- Services ---
export async function getServices(): Promise<ServiceItem[]> {
  return request('/services');
}

export async function createService(token: string, data: Omit<ServiceItem, 'id'>): Promise<ServiceItem> {
  return request('/services', {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function updateService(token: string, id: number, data: Partial<ServiceItem>): Promise<ServiceItem> {
  return request(`/services/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function deleteService(token: string, id: number): Promise<void> {
  return requestVoid(`/services/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

// --- News ---
export async function getNews(): Promise<NewsItem[]> {
  return request('/news');
}

export async function getNewsById(id: number): Promise<NewsItem> {
  return request(`/news/${id}`);
}

export async function createNews(token: string, data: Omit<NewsItem, 'id'>): Promise<NewsItem> {
  return request('/news', {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function updateNews(token: string, id: number, data: Partial<NewsItem>): Promise<NewsItem> {
  return request(`/news/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function deleteNews(token: string, id: number): Promise<void> {
  return requestVoid(`/news/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

// --- Products ---
export async function getProducts(): Promise<Product[]> {
  return request('/products');
}

export async function getProductById(id: number): Promise<Product> {
  return request(`/products/${id}`);
}

export async function createProduct(token: string, data: Omit<Product, 'id' | 'sortOrder'>): Promise<Product> {
  return request('/products', {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function updateProduct(token: string, id: number, data: Partial<Product>): Promise<Product> {
  return request(`/products/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(token: string, id: number): Promise<void> {
  return requestVoid(`/products/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

export async function reorderProducts(token: string, items: { id: number; sortOrder: number }[]): Promise<void> {
  return requestVoid(`/products/reorder`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(items),
  });
}

// --- Orders ---
// submitOrder is PUBLIC — no auth token. The server snapshots productName and
// forces status='new'.
export type OrderSubmission = Pick<
  Order,
  'productId' | 'customerName' | 'phone' | 'email' | 'address' | 'quantity' | 'note'
>;

export async function submitOrder(data: OrderSubmission): Promise<Order> {
  return request('/orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getOrders(token: string): Promise<Order[]> {
  return request('/orders', { headers: getHeaders(token) });
}

export async function updateOrderStatus(token: string, id: number, status: string): Promise<void> {
  return requestVoid(`/orders/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify({ status }),
  });
}

export async function deleteOrder(token: string, id: number): Promise<void> {
  return requestVoid(`/orders/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

// --- Uploads ---
export async function uploadImage(token: string, file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('image', file);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/uploads`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  } catch (e: any) {
    throw new ApiError(e?.message || 'Network error', 0);
  }
  if (!res.ok) {
    // Auth header was sent — flag as authed
    await handleErrorResponse(res, true);
  }
  try {
    return await res.json();
  } catch (e: any) {
    throw new ApiError(e?.message || 'Invalid JSON response', 0);
  }
}

// --- Settings ---
export async function getSetting(key: string): Promise<{ value: string }> {
  return request(`/settings/${key}`);
}

export async function updateSetting(token: string, key: string, value: string): Promise<void> {
  return requestVoid(`/settings/${key}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify({ value }),
  });
}

// --- Categories ---
export async function getCategories(): Promise<Category[]> {
  return request('/categories');
}

export async function getCategoryBySlug(slug: string): Promise<CategoryWithChildren> {
  return request(`/categories/${slug}`);
}

export async function createCategory(token: string, data: Partial<Category>): Promise<Category> {
  return request('/categories', {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function updateCategory(token: string, id: number, data: Partial<Category>): Promise<Category> {
  return request(`/categories/${id}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(token: string, id: number): Promise<void> {
  return requestVoid(`/categories/${id}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

export async function addCategoryImage(token: string, slug: string, data: Partial<CategoryImage>): Promise<CategoryImage> {
  return request(`/categories/${slug}/images`, {
    method: 'POST',
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });
}

export async function updateCategoryImage(token: string, imageId: number, caption: string, orientation?: string): Promise<CategoryImage> {
  return request(`/categories/images/${imageId}`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify({ caption, orientation }),
  });
}

export async function deleteCategoryImage(token: string, imageId: number): Promise<void> {
  return requestVoid(`/categories/images/${imageId}`, {
    method: 'DELETE',
    headers: getHeaders(token),
  });
}

export async function reorderCategoryImages(token: string, slug: string, items: { id: number; sortOrder: number }[]): Promise<void> {
  return requestVoid(`/categories/${slug}/images/reorder`, {
    method: 'PUT',
    headers: getHeaders(token),
    body: JSON.stringify(items),
  });
}
